import { HttpErrorResponse } from '@angular/common/http';

/**
 * Does this failed request mean "the thing does not exist", or "we could not reach the API"?
 *
 * The distinction decides an HTTP status, and getting it wrong is expensive. Every page here used to
 * collapse both cases into `null` with `catchError(() => of(null))`, and a null entity is rendered
 * as a 404. So a backend that was merely slow or briefly overloaded made live pages answer
 * "this page does not exist".
 *
 * That is not hypothetical: the 2026-08-29 crawl recorded 220 store pages as 404 and 20 more as
 * 503/timeout. Re-requesting all 240 afterwards returned 200 for 235 of them — they were live the
 * whole time, and only failed because the crawl was hitting the API faster than it could answer.
 * A 404 tells Google the page is gone and it gets dropped; a 503 tells Google to come back later.
 *
 * Transient (caller should answer 503):
 *   - status 0    — network error, DNS, connection refused, or a timeout. Angular reports all of
 *                   these as 0 because no HTTP response ever arrived.
 *   - status >= 500 — the server answered, but with its own failure.
 *
 * Definitive (caller should answer 404):
 *   - status 404, and any other 4xx — the API was reached and said no such thing exists.
 */
export function isTransientFetchFailure(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    // Not an HTTP failure at all — a parse or client-side error. Treated as transient because it is
    // not evidence that the entity is missing, and 503 is the safer wrong answer of the two.
    return true;
  }
  return error.status === 0 || error.status >= 500;
}
